
import { cn } from '@fullcalendar/core/internal-common';
import { z } from 'zod';

export const flattenZodErrors = (error: z.ZodError): Record<string, string> => {
  const flattened: Record<string, string> = {};
  error.issues.forEach(issue => {
    const path = issue.path.join('.');
    flattened[path] = issue.message;
  });
  return flattened;
};
const priorityOptions = ['Low', 'Medium', 'High']; // Adjust as per your app

const calibrationFrequencyOptions = ['Monthly', 'Quarterly', 'Yearly', 'Custom'];

const customFrequencyRegex = /^\d+$/;

// --- Shared Fields ---
export const contactEmailSchema = z
  .string()
  .email('Please enter a valid email address.');

export const contactNumberSchema = z
  .string()
  .regex(/^(\+91[-\s]?[6-9]\d{9}|0[6-9]\d{9}|[6-9]\d{9})$/, 'Please enter a valid contact number: can start with +91, +91-, +91␣, 0, or directly with 10 digits.');

export const nameSchema = (field: string) =>
  z
    .string()
    .min(3, `${field} must be at least 3 characters long.`)
    .max(50, `${field} must be at most 50 characters long.`)
    .regex(/^[A-Za-z\s]+$/, `${field} can only contain letters and spaces.`);


export const firstNameSchema = z
  .string()
  .min(3, "First name must be at least 3 characters long")
  .max(50, "First name must be at most 50 characters long")
  .regex(/^[A-Za-z\s]+$/, "First name can only contain letters and spaces")
// .regex(/^[A-Z][a-zA-Z\s]*$/, "First name must start with a capital letter");

export const lastNameSchema = z
  .string()
  .min(3, "Last name must be at least 3 characters long")
  .max(50, "Last name must be at most 50 characters long")
  .regex(/^[A-Za-z\s]+$/, "Last name can only contain letters and spaces")
// .regex(/^[A-Z][a-zA-Z\s]*$/, "Last name must start with a capital letter");


// password rule
export const loginPasswordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .max(20, "Password must not exceed 20 characters")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[0-9]/, "Password must contain at least one number")
  .regex(/[@$!%*?&]/, "Password must contain at least one special character");


export const signUpSchema = z
  .object({
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    email: contactEmailSchema,
    password: loginPasswordSchema,
    userRole: z.string().min(1, "Role is required"),
    contactNumber: contactNumberSchema,
    // confirmPassword: z.string(),
  })

export type SignUpPayload = z.infer<typeof signUpSchema>;

export const AdduserSchema = z
  .object({
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    email: contactEmailSchema,
    passwordHash: loginPasswordSchema,
    userRole: z.string().min(1, "Role is required"),
    contactNumber: contactNumberSchema,
    OrgId: z.string().optional(),
    LocationId: z.string().optional(),
    DepartmentId: z.string().optional(),
    DepartmentName: z.string().optional(),
    Location: z.string().optional(),  
    OrgName: z.string().optional(),
  })
export type AdduserPayload = z.infer<typeof AdduserSchema>

// --- Vendor Schema ---
export const vendorSchema = z.object({
  name: z
    .string()
    .min(3, 'Vendor name must be at least 3 characters long.')
    .regex(/^[A-Za-z0-9\s&.]+$/, 'Vendor name can only contain letters, numbers, spaces, & and .'),
  contactEmail: contactEmailSchema,
  contactNumber: contactNumberSchema,
  organization: z.string().optional(),
  status: z
    .string()
    .optional()
    .refine(
      (val) => val === undefined || val === 'Active' || val === 'Inactive',
      { message: 'Please select a status.' }
    ),
  gsTin: z
    .string()
    .regex(/^[A-Za-z0-9]{15}$/, 'GSTIN must be exactly 15 alphanumeric characters.'),
  address: z.object({
    locationName: z.string().min(1, 'Address line is required.'),
    street: z.string().min(1, 'Street name is required.'),
    city: z.string().regex(/^[A-Za-z\s]+$/, 'City name must contain only letters.').min(1, 'City name is required.'),
    state: z.string().regex(/^[A-Za-z\s]+$/, 'State name must contain only letters.').min(1, 'State name is required.'),
    zipCode: z.string().regex(/^\d{6}$/, 'Zip code must be exactly 6 digits.'),
    country: z.string().regex(/^[A-Za-z\s]+$/, 'Country name must contain only letters.').min(1, 'Country name is required.'),
  }),
});

// --- Product Schema ---
export const productSchema = z
  .object({
    selectedVendor: z
      .object({
        vendorId: z.string().min(1, 'Vendor is required.'),
        name: z.string(),
      })
      .nullable()
      .refine((v) => v !== null, { message: 'Vendor is required.' }),
    name: z.string().min(3, 'Product Name is required.'),
    type: z.string().min(3, 'Product Type is required.'),
    description: z.string().min(1, 'Product Description is required.'),
    productStatus: z.string().min(1, 'Product Status is required.'),
    productImage: z.string().optional(),
    maintenanceSchedule: z.string().optional(),
    location: z.string().optional(),
    hasAMC: z.boolean(),
    scheduledDate: z.string().optional(),
  })

//   // --- Edit Product Schema ---
export const editProductSchema = z
  .object({
    name: z.string().min(3, 'Product Name must be at least 3 characters.').optional(),
    type: z.string().min(3, 'Product Type must be at least 3 characters.').optional(),
    description: z.string().min(1, 'Product Description cannot be empty.').optional(),
    productStatus: z.string().min(1, 'Product Status is required.').optional(),
    maintenanceSchedule: z
      .string()
      .optional()
      .refine((val) => !val || val.trim().length > 0, {
        message: 'Maintenance Schedule cannot be empty.',
      }),
    location: z
      .string()
      .optional()
      .refine((val) => !val || val.trim().length > 0, {
        message: 'Location cannot be empty.',
      }),
    hasAMC: z.boolean().optional(),
    scheduledDate: z.string().optional(),
  })
 
// --- Update User Schema ---
export const updateUserSchema = z.object({
  firstName: z
    .string()
    .min(3, "First name must be at least 3 characters long")
    .regex(/^[A-Za-z\s]+$/, "First name can only contain letters and spaces"),
  lastName: z
    .string()
    .min(3, "Last name must be at least 3 characters long")
    .regex(/^[A-Za-z\s]+$/, "Last name can only contain letters and spaces"),
  email: z.string().email("Invalid email address"),
  userRole: z.string().min(1, "Role is required"),
  contactNumber: contactNumberSchema.optional(),
  scope: z.string().optional(),
  tenantId: z.string().optional(),
  vendorId: z.string().optional(),
});

export const passwordSchema = z.object({
  oldPassword: z.string().min(6, "Old password must be at least 6 characters"),
  newPassword: z
    .string()
    .min(8, "New password must be at least 8 characters")
    .max(20, "Password must not exceed 20 characters")
    .regex(/[A-Z]/, "Must contain at least one uppercase letter")
    .regex(/[a-z]/, "Must contain at least one lowercase letter")
    .regex(/[0-9]/, "Must contain at least one number")
    .regex(/[@$!%*?&]/, "Must contain at least one special character"),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

export const resetPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, "New password must be at least 8 characters")
      .max(20, "Password must not exceed 20 characters")
      .regex(/[A-Z]/, "Must contain at least one uppercase letter")
      .regex(/[a-z]/, "Must contain at least one lowercase letter")
      .regex(/[0-9]/, "Must contain at least one number")
      .regex(/[@$!%*?&]/, "Must contain at least one special character"),

    confirmPassword: z
      .string()
      .min(1, "Confirm password is required"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });


export const assetEquipmentIdSchema = z
  .string()
  .min(1, "Asset Equipment ID is required")
  .max(15, "Asset Equipment ID must be at most 15 characters")
  .regex(
    /^[A-Za-z0-9]+$/,
    "Asset Equipment ID must contain only letters and numbers"
  );
//----- Add Asset -----
export const assetSchema = z.object({
  assetNameId: z.string().min(1, 'Asset Name is required. Please select a vendor product.'),
  assetEquipmentId: assetEquipmentIdSchema,
  name: z.string().min(1, 'Asset Name is required.'),
  type: z.string().min(1, 'Asset Type is required.'),
  subType: z.string().optional(),
  orgId: z.string().min(1, "Organization is required."),
  orgName: z.string().min(1, "Organization Name is required."),
  locationId: z.string().min(1, "Location is required."),
  location: z.string().min(1, "Location Name is required."),
  departmentId: z.string().min(1, "Department is required."),
  departmentName: z.string().min(1, "Department Name is required."),
  vendorId: z.string().min(1, "Vendor is required."),
  vendorName: z.string().min(1, "Vendor Name is required."),
  installedDate: z.string()
    .min(1, "Installed Date is required.")
    .refine((date) => date <= new Date().toISOString().split("T")[0], {
      message: "Installed Date cannot be in the future.",
    }),
  status: z.string().min(1, "Status is required."),
});

// --- Edit Asset Schema ---
export const editAssetSchema = z.object({
  assetEquipmentId: assetEquipmentIdSchema,
  id: z.string().min(1, "Asset ID is required."),
  assetName: z.string().min(1, "Asset Name is required."),
  assetType: z.string().min(1, "Asset Type is required."),
  assetSubType: z.string().optional(),
  status: z.string().min(1, "Status is required."),
  // installedDate: z.string()
  //   .min(1, "Installed Date is required.")
  //   .refine((date) => date <= new Date().toISOString().split("T")[0], {
  //     message: "Installed Date cannot be in the future.",
  //   }),
});

// --- Organization Schema ---
export const organizationSchema = z.object({
  name: z
    .string()
    .min(3, 'Organization name must be at least 3 characters long.')
    .regex(/^[A-Za-z\s]+$/, 'Organization name can only contain letters and spaces.'),
  email: contactEmailSchema,
  contact: contactNumberSchema,
  hasAMC: z.boolean(),
  status: z.string().min(1, 'Please select a status.'),
  gsTin: z
    .string()
    .regex(/^[A-Za-z0-9]{15}$/, 'GSTIN must be exactly 15 alphanumeric characters.'),
});

// --- Notification Schema ---
export const notificationSchema = z.object({
  recipient: z.string().email('Recipient must be a valid email.'),
  message: z.string().min(1, 'Message is required.'),
  vendorId: z.string().min(1, 'Vendor is required.'),
  assetId: z.string().min(1, 'Asset is required.'),
  // status: z.string().optional(),
  organizationId: z.string().optional(),
  calibrationId: z.string().optional(),
  reminderCount: z.number().min(0).default(0),
});

// ---------------- Plant Schema ----------------
export const plantSchema = z.object({
  name: z
    .string()
    .min(3, 'Plant name must be at least 3 characters long.')
    .regex(/^[A-Za-z\s]+$/, 'Plant name can only contain letters and spaces.'),
  address: z.object({
    street: z.string().min(1, 'Street is required.'),
    city: z
      .string()
      .regex(/^[A-Za-z\s]+$/, 'City name must contain only letters.')
      .min(1, 'City is required.'),
    state: z
      .string()
      .regex(/^[A-Za-z\s]+$/, 'State name must contain only letters.')
      .min(1, 'State is required.'),
    zipCode: z
      .string()
      .regex(/^\d{6}$/, 'ZIP code must be exactly 6 digits.'),
    country: z
      .string()
      .regex(/^[A-Za-z\s]+$/, 'Country name must contain only letters.')
      .min(1, 'Country is required.'),
  }),
  isActive: z
    .boolean()
    .refine((val) => val === true || val === false, {
      message: 'Please select a status.',
    }),

});


// --- Department Schema ---
export const departmentSchema = z.object({
  departmentName: z.string().min(1, 'Department Selection is required'),
  locationId: z.string().min(1, 'Plant selection is required.'),
  locationName: z.string().min(1, 'Plant name is required.'),
});

export const calibrationSchema = z
  .object({
    contactName: nameSchema,

    contactNumber: contactNumberSchema,

    calibrationDate: z
      .string()
      .min(1, 'Calibration Date is required.')
      .refine(
        (date) => date <= new Date().toISOString().split('T')[0],
        'Calibration Date cannot be in the future.'
      ),

    priority: z
      .string()
      .refine((val) => priorityOptions.includes(val), {
        message: `Priority must be one of: ${priorityOptions.join(', ')}`,
      }),

    calibrationFrequency: z
      .string()
      .refine((val) => calibrationFrequencyOptions.includes(val), {
        message: `Calibration Frequency must be one of: ${calibrationFrequencyOptions.join(', ')}`,
      }),

    customFrequency: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.calibrationFrequency === 'Custom') {
      if (!data.customFrequency || !customFrequencyRegex.test(data.customFrequency.trim())) {
        ctx.addIssue({
          path: ['customFrequency'],
          code: z.ZodIssueCode.custom,
          message: 'Custom Frequency must be a number of days, e.g., "30".',
        });
      }
    }
  });